import type { MachineConfig } from 'xstate';
import { createMachine as machineGenerator } from 'xstate';

import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { SplitClient } from 'src/modules/designStudio/machines/building';
import { Mode, Partner } from 'src/modules/designStudio/providers';

import {
  addThumbnailsToMenuItems,
  filterColors,
  filterMenuItems,
  filterTextures,
} from './helpers';
import { options } from './options';
import type {
  Event,
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  Schema,
  Textures,
  UpdateExperimentsEvent,
} from './types';
import { Context } from './types';

const createMachineConfig = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  mode: Mode = Mode.DEFAULT,
  // eslint-disable-next-line @typescript-eslint/default-param-last
  partner: Partner = Partner.DEFAULT,
  splitClient: SplitClient,
  jobId?: number,
  shareToken?: string,
): MachineConfig<Context, Schema, Event> => ({
  id: 'product-catalog',
  context: {
    filteredColors: {
      [DesignConfigCategories.ROOF]: [],
      [DesignConfigCategories.WALL]: [],
      [DesignConfigCategories.TRIM]: [],
      [DesignConfigCategories.WINDOW]: [],
    },
    filteredTextures: {},
    filterTracker: {},
    geometry: null,
    hasUpdatedExperiments: false,
    jobId,
    menuItems: [],
    metadata: null,
    mode,
    partner,
    shareToken,
    shouldShowJhMagnolia: false,
    shouldShowPartnerProducts: false,
    splitClient,
    textures: null,
  },
  initial: 'unknown',
  states: {
    unknown: {
      always: [
        { target: 'fetchModelData', cond: 'isGuestMode' },
        { target: 'fetchModelData', cond: 'isPreviewMode' },
        { target: 'fetchModelData', cond: 'isShareMode' },
        { target: 'fetchTextures', cond: 'isInteractiveRenderingMode' },
        { target: 'fetchProductSettingOrgId' },
      ],
    },
    fetchProductSettingOrgId: {
      invoke: {
        src: 'fetchProductSettingOrgId',
        onDone: {
          target: 'fetchProductSettings',
          actions: ['updateProductSettingsOrgId'],
        },
        onError: {
          target: 'fetchModelData',
          actions: ['logError'],
        },
      },
    },
    fetchProductSettings: {
      invoke: {
        src: 'fetchProductSettings',
        onDone: {
          actions: 'updateProductSettings',
          target: 'fetchModelData',
        },
        onError: { target: 'fetchModelData', actions: 'logError' },
      },
    },
    fetchModelData: {
      invoke: {
        src: 'fetchModelData',
        onDone: {
          target: 'fetchTextures',
          actions: ['updateModelData'],
        },
        onError: {
          target: 'fetchTextures',
          actions: ['logError'],
        },
      },
    },
    fetchTextures: {
      invoke: {
        src: 'fetchTextures',
        onDone: [
          {
            target: 'fetchRunningExperiments',
            actions: 'assignTextures',
          },
        ],
        onError: {
          target: 'end',
          actions: 'logError',
        },
      },
    },
    fetchRunningExperiments: {
      invoke: {
        src: 'fetchRunningExperiments',
        onDone: [
          {
            target: 'setMenuItems',
            actions: 'setExperiments',
            cond: 'isGuestMode',
          },
          {
            target: 'setMenuItems',
            actions: 'setExperiments',
            cond: 'isPreviewMode',
          },
          {
            target: 'setMenuItems',
            actions: 'setExperiments',
            cond: 'isShareMode',
          },
          {
            target: 'fetchPartner',
            actions: 'setExperiments',
          },
        ],
        onError: {
          target: 'setMenuItems',
          actions: 'logError',
        },
      },
    },
    fetchPartner: {
      invoke: {
        src: 'fetchPartner',
        onDone: [
          {
            target: 'setMenuItems',
            actions: 'setPartner',
          },
        ],
        onError: {
          target: 'setMenuItems',
          actions: 'logError',
        },
      },
    },
    setMenuItems: {
      invoke: {
        src: 'fetchMenuItems',
        onDone: [
          {
            target: 'idle',
            actions: ['setMenuItems'],
          },
        ],
        onError: {
          target: 'idle',
          actions: 'logError',
        },
      },
    },
    idle: {
      on: {
        UPDATE_EXPERIMENTS_EVENT: [
          {
            target: 'setMenuItems',
            actions: ['setExperiments', 'setHasUpdatedExperiments'],
          },
        ],
      },
    },
    end: {
      type: 'final',
    },
  },
});

const createMachine = (
  mode: Mode,
  partner: Partner,
  splitClient: SplitClient,
  jobId?: number,
  shareToken?: string,
) =>
  machineGenerator(
    createMachineConfig(mode, partner, splitClient, jobId, shareToken),
    options,
  );

export {
  Textures,
  addThumbnailsToMenuItems,
  Context,
  createMachine,
  createMachineConfig,
  Event,
  filterColors,
  FilteredColorsData,
  FilteredTexturesData,
  filterMenuItems,
  filterTextures,
  FilterTracker,
  options,
  UpdateExperimentsEvent,
};
